import React from 'react'
import { TOKEN } from 'shared/constants/app'
import { API_URL } from 'shared/constants/urls'

type NativeWindow = Window & {
  ReactNativeWebView?: { postMessage: (message: string) => void }
}

const RETRY_DELAYS_MS = [350, 900, 1800]

const notifyApp = (bridgeId: string | null, type: 'ready' | 'received' | 'error', code?: string, retryable?: boolean) => {
  (window as NativeWindow).ReactNativeWebView?.postMessage(JSON.stringify({
    type, bridge: bridgeId, code, retryable
  }))
}

class EntryError extends Error {
  constructor(public code: string, public retryable: boolean) {
    super(code)
  }
}

const wait = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms) })

const fetchWithTimeout = async (url: string, options: RequestInit): Promise<Response> => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 12000)
  try {
    return await fetch(url, { ...options, signal: controller.signal })
  } finally {
    clearTimeout(timer)
  }
}

const tokenUserId = (token: string): string | null => {
  try {
    const encoded = token?.split('.')[1]
    if (!encoded) return null
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/')
      .padEnd(Math.ceil(encoded.length / 4) * 4, '=')
    return JSON.parse(atob(base64))._id ?? null
  } catch {
    return null
  }
}

export const refreshSession = async (expectedUserId: string): Promise<string> => {
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const response = await fetchWithTimeout(`${API_URL}refresh`, {
        credentials: 'include',
        headers: { 'X-Client-App': 'admin' }
      })
      if (response.ok) {
        const data = await response.json()
        if (!data?.access_token || tokenUserId(data.access_token) !== expectedUserId) {
          throw new EntryError('account_mismatch', false)
        }
        return data.access_token
      }
      if (response.status === 403) throw new EntryError('session_forbidden', false)
      if (response.status !== 401 && response.status !== 408 && response.status !== 429 && response.status < 500) {
        throw new EntryError('session_rejected', false)
      }
    } catch (error) {
      if (error instanceof EntryError) throw error
    }
    if (attempt < RETRY_DELAYS_MS.length) await wait(RETRY_DELAYS_MS[attempt])
  }
  throw new EntryError('session_unavailable', true)
}

export const MobileCabinetEntry: React.FC = () => {
  const bridgeId = React.useMemo(() => new URLSearchParams(window.location.search).get('hs_bridge'), [])
  const [status, setStatus] = React.useState('Открываем кабинет продавца…')
  const redeeming = React.useRef(false)

  React.useEffect(() => {
    let active = true
    let stage: 'ready' | 'received' | 'error' = 'ready'
    let failure: EntryError | null = null
    const handleTicket = async (event: Event) => {
      if (redeeming.current) return
      const detail = (event as CustomEvent<{ ticket?: string, bridge?: string }>).detail
      if (bridgeId && detail?.bridge !== bridgeId) return
      const ticket = detail?.ticket
      if (!ticket || !/^[a-f\d]{64}$/i.test(ticket)) return
      redeeming.current = true
      stage = 'received'
      notifyApp(bridgeId, 'received')
      setStatus('Входим в кабинет продавца…')
      try {
        // Пропуск одноразовый: повторять POST с ним нельзя. При сбое приложение
        // открывает новую попытку и запрашивает новый пропуск.
        const response = await fetchWithTimeout(`${API_URL}seller-cabinet-webview/redeem`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-Client-App': 'admin' },
          body: JSON.stringify({ ticket })
        })
        if (!response.ok) {
          throw new EntryError('redeem_failed', [401, 408, 429].includes(response.status) || response.status >= 500)
        }
        const redeemed = await response.json()
        if (!redeemed?.user_id) throw new EntryError('redeem_invalid', true)
        const accessToken = await refreshSession(String(redeemed.user_id))
        if (!active) return
        // На следующей странице useAuth ещё раз сверит пользователя с cookie.
        localStorage.setItem(TOKEN, accessToken)
        const ordersUrl = bridgeId ? `/orders?hs_bridge=${encodeURIComponent(bridgeId)}` : '/orders'
        window.location.replace(ordersUrl)
      } catch (error) {
        if (!active) return
        const entryError = error instanceof EntryError ? error : new EntryError('network_error', true)
        failure = entryError
        stage = 'error'
        setStatus('Не удалось войти в кабинет. Повторите попытку в приложении.')
        notifyApp(bridgeId, 'error', entryError.code, entryError.retryable)
      }
    }

    window.addEventListener('hs-seller-cabinet-ticket', handleTicket)
    notifyApp(bridgeId, 'ready')
    // Сообщение может потеряться, пока iOS/Android подключает мост WebView.
    const readyTimer = window.setInterval(() => {
      if (stage === 'error' && failure) notifyApp(bridgeId, 'error', failure.code, failure.retryable)
      else notifyApp(bridgeId, stage)
    }, 1000)
    return () => {
      active = false
      window.clearInterval(readyTimer)
      window.removeEventListener('hs-seller-cabinet-ticket', handleTicket)
    }
  }, [bridgeId])

  return (
    <main style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, boxSizing: 'border-box', textAlign: 'center', background: '#fff'
    }}>
      {status}
    </main>
  )
}
